'use client';

import { useCallback, useRef, useState } from 'react';
import { cn } from '../../../../lib/utils';
import { InspectorControlsStyles as S } from '../../../primitives/form-field/inspector-controls-styles';
import ValueInputField from '../../../primitives/form-field/value-input-field/ValueInputField';
import { ArrowDropDown, CheckCircleFill } from '@crabs-ai/web-icons';
import { ColorFormat, useColorPickerContext } from './color-picker-context';

export interface ColorPickerInputProps {
  className?: string;
}

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const match = /^#?([0-9A-Fa-f]{6})$/.exec(hex);
  if (!match) return null;
  const n = Number.parseInt(match[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

const rgbToHsb = (
  r: number,
  g: number,
  b: number,
): { h: number; s: number; b: number } => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d + 6) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    b: Math.round(max * 100),
  };
};

const hsbToRgb = (
  h: number,
  s: number,
  b: number,
): { r: number; g: number; b: number } => {
  const sn = s / 100;
  const bn = b / 100;
  const c = bn * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = bn - c;
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (h < 60) {
    r1 = c;
    g1 = x;
  } else if (h < 120) {
    r1 = x;
    g1 = c;
  } else if (h < 180) {
    g1 = c;
    b1 = x;
  } else if (h < 240) {
    g1 = x;
    b1 = c;
  } else if (h < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
};

const formatColor = (hex: string, format: ColorFormat): string => {
  if (format === 'hex') return hex.toUpperCase();
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  if (format === 'rgb') return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  const hsb = rgbToHsb(rgb.r, rgb.g, rgb.b);
  return `${hsb.h}°, ${hsb.s}%, ${hsb.b}%`;
};

const expandShortHex = (input: string): string => {
  const clean = input.replace(/^#/, '').toUpperCase();
  if (!/^[0-9A-F]+$/.test(clean) || clean.length === 0 || clean.length > 6)
    return input;
  switch (clean.length) {
    case 1:
      return `#${clean.repeat(6)}`;
    case 2:
      return `#${clean.repeat(3)}`;
    case 3:
      return `#${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`;
    case 6:
      return `#${clean}`;
    default:
      // 4-5자리는 유효하지 않은 HEX - 0으로 패딩
      return `#${clean.padEnd(6, '0')}`;
  }
};

const parseToHex = (input: string, format: ColorFormat): string | null => {
  const trimmed = input.trim();
  if (format === 'hex') {
    const expanded = expandShortHex(trimmed);
    if (/^#[0-9A-Fa-f]{6}$/.test(expanded)) return expanded.toUpperCase();
    return null;
  }
  const parts = trimmed
    .split(/[,\s°%]+/)
    .filter(Boolean)
    .map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) return null;
  if (format === 'rgb') return rgbToHex(parts[0]!, parts[1]!, parts[2]!);
  const rgb = hsbToRgb(parts[0]!, parts[1]!, parts[2]!);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

const FORMAT_LABELS: Record<ColorFormat, string> = {
  hex: 'HEX',
  rgb: 'RGB',
  hsb: 'HSB',
};

const FORMAT_ORDER: ColorFormat[] = ['hex', 'rgb', 'hsb'];

const CONTAINER_INTERACTION_STYLES = cn(
  S.container,
  'overflow-hidden transition-colors duration-200 ease-out',
  'hover:has-not-[input:disabled]:has-not-[input:focus]:bg-sys-style-neutral-light',
  'active:has-not-[input:disabled]:has-not-[input:focus]:bg-sys-style-neutral-default',
  'has-[input:focus]:border has-[input:focus]:border-sys-style-primary-default has-[input:focus]:bg-sys-style-inverse-light',
  'has-[input:disabled]:bg-sys-style-neutral-lightest',
);

const INPUT_STYLES = cn(
  'flex w-full transition-all outline-none',
  'text-comp-moderate-medium',
  'text-sys-cont-neutral-default',
  'placeholder:text-sys-cont-neutral-disabled-default',
  'caret-sys-style-primary-default',
  'bg-transparent',
  'cursor-default',
  'focus:placeholder:text-sys-cont-neutral-disabled-default',
  'disabled:text-sys-cont-neutral-disabled-light',
  'disabled:pointer-events-none disabled:cursor-not-allowed',
);

export function ColorPickerInput({ className }: ColorPickerInputProps) {
  const {
    mode,
    value,
    isNone,
    opacity,
    onOpacityChange,
    onChangeEnd,
    onOpacityChangeEnd,
    format,
    onFormatChange,
    onChange,
    lastSolidColor,
    setLastSolidColor,
  } = useColorPickerContext();

  const [isFormatMenuOpen, setIsFormatMenuOpen] = useState(false);
  const [colorFocused, setColorFocused] = useState(false);
  const [colorInputValue, setColorInputValue] = useState('');
  const colorInputRef = useRef<HTMLInputElement>(null);

  const baseColor =
    mode === 'solid' && value && value.startsWith('#') ? value : lastSolidColor;
  const displayValue = isNone
    ? 'None'
    : colorFocused
      ? colorInputValue
      : formatColor(baseColor, format);
  const opacityPercent = Math.round(opacity * 100);
  const rgb = hexToRgb(baseColor) ?? { r: 255, g: 255, b: 255 };
  const hsb = rgbToHsb(rgb.r, rgb.g, rgb.b);

  const applyColor = useCallback(
    (input: string): boolean => {
      const hex = parseToHex(input, format);
      if (hex) {
        setLastSolidColor(hex);
        onChange(hex);
        return true;
      }
      return false;
    },
    [format, onChange, setLastSolidColor],
  );

  const handleColorFocus = useCallback(() => {
    setColorFocused(true);
    setColorInputValue(formatColor(baseColor, format));
    requestAnimationFrame(() => colorInputRef.current?.select());
  }, [baseColor, format]);

  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setColorInputValue(raw);
      applyColor(raw);
    },
    [applyColor],
  );

  const handleColorBlur = useCallback(() => {
    setColorFocused(false);
    const applied = applyColor(colorInputValue);
    if (applied) {
      const hex = parseToHex(colorInputValue, format);
      if (hex) onChangeEnd?.(hex);
    } else {
      setColorInputValue(formatColor(baseColor, format));
    }
  }, [colorInputValue, applyColor, baseColor, format, onChangeEnd]);

  const handleColorKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const applied = applyColor(colorInputValue);
        if (applied) {
          const hex = parseToHex(colorInputValue, format);
          if (hex) onChangeEnd?.(hex);
          colorInputRef.current?.blur();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setColorInputValue(formatColor(baseColor, format));
        colorInputRef.current?.blur();
      }
    },
    [applyColor, colorInputValue, baseColor, format, onChangeEnd],
  );

  const toNumericInput = useCallback((v: number | string) => {
    const next = typeof v === 'string' ? Number.parseInt(v, 10) : v;
    if (Number.isNaN(next)) return null;
    return next;
  }, []);

  const handleRgbChannelChange = useCallback(
    (channel: 'r' | 'g' | 'b', nextValue: number | string) => {
      const parsed = toNumericInput(nextValue);
      if (parsed == null) return;

      const nextRgb = {
        r: channel === 'r' ? parsed : rgb.r,
        g: channel === 'g' ? parsed : rgb.g,
        b: channel === 'b' ? parsed : rgb.b,
      };
      const nextHex = rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b);
      setLastSolidColor(nextHex);
      onChange(nextHex);
      onChangeEnd?.(nextHex);
    },
    [
      onChange,
      onChangeEnd,
      rgb.b,
      rgb.g,
      rgb.r,
      setLastSolidColor,
      toNumericInput,
    ],
  );

  const handleHsbChannelChange = useCallback(
    (channel: 'h' | 's' | 'b', nextValue: number | string) => {
      const parsed = toNumericInput(nextValue);
      if (parsed == null) return;

      const nextHsb = {
        h: channel === 'h' ? Math.max(0, Math.min(360, parsed)) : hsb.h,
        s: channel === 's' ? Math.max(0, Math.min(100, parsed)) : hsb.s,
        b: channel === 'b' ? Math.max(0, Math.min(100, parsed)) : hsb.b,
      };
      const nextRgb = hsbToRgb(nextHsb.h, nextHsb.s, nextHsb.b);
      const nextHex = rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b);
      setLastSolidColor(nextHex);
      onChange(nextHex);
      onChangeEnd?.(nextHex);
    },
    [
      hsb.b,
      hsb.h,
      hsb.s,
      onChange,
      onChangeEnd,
      setLastSolidColor,
      toNumericInput,
    ],
  );

  return (
    <div
      className={cn(
        'flex items-center gap-4xs',
        isNone && 'pointer-events-none opacity-50',
        className,
      )}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Format selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsFormatMenuOpen((v) => !v)}
          className="flex items-center gap-5xs rounded-3xs p-4xs text-comp-moderate-medium text-sys-cont-neutral-light transition-colors hover:bg-sys-style-neutral-lightest"
        >
          {FORMAT_LABELS[format]}
          <ArrowDropDown
            className={cn(
              'size-[20px] transition-transform duration-200 [&_path]:fill-sys-cont-neutral-light',
              isFormatMenuOpen && 'rotate-180',
            )}
          />
        </button>
        {isFormatMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsFormatMenuOpen(false)}
            />
            <div className="absolute left-0 top-[40px] z-20 min-w-[120px] overflow-hidden rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface-1 px-2xs py-sm shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)]">
              {FORMAT_ORDER.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    onFormatChange(f);
                    setIsFormatMenuOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-xs px-sm py-xs text-comp-moderate-medium leading-[1.7] transition-colors',
                    f === format
                      ? 'text-sys-cont-primary-default'
                      : 'text-sys-cont-neutral-default hover:bg-sys-bg-surface-1-hover',
                  )}
                >
                  {FORMAT_LABELS[f]}
                  <CheckCircleFill
                    className={cn(
                      'size-[24px] transition-opacity',
                      f === format
                        ? 'opacity-100 [&_path]:fill-sys-cont-primary-default'
                        : 'opacity-0',
                    )}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {format === 'hex' ? (
        <div className={cn(CONTAINER_INTERACTION_STYLES, 'min-w-0 flex-1')}>
          <div
            className={cn(
              S.leadingNode.colorChip,
              isNone && S.leadingNode.colorChipDisabled,
            )}
            style={{
              backgroundColor: isNone ? 'transparent' : baseColor,
            }}
          />
          <input
            ref={colorInputRef}
            value={displayValue}
            disabled={isNone}
            onChange={handleColorChange}
            onFocus={handleColorFocus}
            onBlur={handleColorBlur}
            onKeyDown={handleColorKeyDown}
            className={cn(INPUT_STYLES, 'min-w-0')}
          />
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1">
            <ValueInputField
              value={format === 'rgb' ? rgb.r : hsb.h}
              onChange={(v) =>
                format === 'rgb'
                  ? handleRgbChannelChange('r', v)
                  : handleHsbChannelChange('h', v)
              }
              min={0}
              max={format === 'rgb' ? 255 : 360}
              step={1}
              showStepper={false}
              disabled={isNone}
              enableDrag={false}
            />
          </div>
          <div className="min-w-0 flex-1">
            <ValueInputField
              value={format === 'rgb' ? rgb.g : hsb.s}
              onChange={(v) =>
                format === 'rgb'
                  ? handleRgbChannelChange('g', v)
                  : handleHsbChannelChange('s', v)
              }
              min={0}
              max={format === 'rgb' ? 255 : 100}
              step={1}
              showStepper={false}
              disabled={isNone}
              enableDrag={false}
            />
          </div>
          <div className="min-w-0 flex-1">
            <ValueInputField
              value={format === 'rgb' ? rgb.b : hsb.b}
              onChange={(v) =>
                format === 'rgb'
                  ? handleRgbChannelChange('b', v)
                  : handleHsbChannelChange('b', v)
              }
              min={0}
              max={format === 'rgb' ? 255 : 100}
              step={1}
              showStepper={false}
              disabled={isNone}
              enableDrag={false}
            />
          </div>
        </>
      )}

      {/* Opacity — ValueInputField 직접 사용 */}
      <div className="w-[64px] shrink-0">
        <ValueInputField
          value={opacityPercent}
          onChange={(v) => {
            const num = typeof v === 'string' ? Number.parseInt(v, 10) : v;
            if (!Number.isNaN(num)) {
              const next = Math.max(0, Math.min(100, num)) / 100;
              onOpacityChange(next);
              onOpacityChangeEnd?.(next);
            }
          }}
          isUnit="%"
          min={0}
          max={100}
          step={1}
          defaultValue={100}
          showStepper={false}
          disabled={isNone}
          enableDrag={false}
        />
      </div>
    </div>
  );
}
